import { useEffect, useMemo, useState } from 'react';
import { Save } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { Textarea } from '@/components/form/Textarea';
import { SmartSelect } from '@/components/form/SmartSelect';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import { createPr, updatePr, type Pr, type PrItem } from '@/api/pr';
import type { Authority } from '@/api/mrn';
import { listItems } from '@/api/items';
import { listMrns, type Mrn } from '@/api/mrn';
import { listParties, type Party } from '@/api/parties';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { formatINR, todayISO } from '@/lib/format';

interface Props {
  open: boolean;
  editing: Pr | null;
  onClose: () => void;
}

type FormState = {
  prDate: string;
  subject: string;
  mrnId: number | '';
  vendorId: number | '';
  department: string;
  authority: Authority;
  remarks: string;
  items: PrItem[];
};

function emptyForm(): FormState {
  return {
    prDate: todayISO(),
    subject: '',
    mrnId: '',
    vendorId: '',
    department: '',
    authority: 'HOD',
    remarks: '',
    items: [{ itemName: '', qty: '', unit: '', unitPrice: '' }],
  };
}

function fromPr(p: Pr): FormState {
  return {
    prDate: p.prDate.slice(0, 10),
    subject: p.subject ?? '',
    mrnId: p.mrnId ?? '',
    vendorId: p.vendorId ?? '',
    department: p.department,
    authority: p.authority,
    remarks: p.remarks ?? '',
    items: p.items.length
      ? p.items.map((it) => ({
          itemId: it.itemId,
          itemName: it.itemName,
          qty: it.qty,
          unit: it.unit,
          unitPrice: it.unitPrice ?? 0,
        }))
      : [{ itemName: '', qty: '', unit: '', unitPrice: '' }],
  };
}

export function PrFormModal({ open, editing, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'department'>();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [triedSave, setTriedSave] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? fromPr(editing) : emptyForm());
    setTriedSave(false);
  }, [open, editing]);

  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, status: 'Active' }],
    queryFn: () => listItems({ pageSize: 10000, status: 'Active' }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const itemMaster = useMemo(() => itemsResp?.data ?? [], [itemsResp]);
  /** Purchase rate from the item master, or '' when none is set. */
  const purchaseRateOf = (itemId?: number | null) => {
    const rate = Number(itemMaster.find((i) => i.id === itemId)?.purchaseRate ?? 0);
    return rate > 0 ? rate : '';
  };

  const { data: mrnsResp } = useQuery({
    queryKey: ['mrn', { status: 'Approved', noPr: true }],
    queryFn: () => listMrns({ status: 'Approved', pageSize: 200, noPr: true }),
    enabled: open,
    staleTime: 60_000,
  });
  const approvedMrns: Mrn[] = useMemo(() => mrnsResp?.data ?? [], [mrnsResp]);

  const { data: vendorsResp } = useQuery({
    queryKey: ['parties', { type: 'vendor', pageSize: 10000 }],
    queryFn: () => listParties({ type: 'vendor', pageSize: 10000 }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const vendors: Party[] = useMemo(() => vendorsResp?.data ?? [], [vendorsResp]);
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name, sublabel: v.gstin ?? undefined })),
    [vendors],
  );

  const editingId = editing?.id ?? null;
  const isReadOnly =
    editing && editing.status !== 'Draft' && editing.status !== 'Pending';

  const total = useMemo(
    () =>
      form.items.reduce(
        (acc, it) => acc + Number(it.qty || 0) * Number(it.unitPrice || 0),
        0,
      ),
    [form.items],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = toPayload(form);
      return editingId ? updatePr(editingId, payload) : createPr(payload);
    },
    onSuccess: (pr) => {
      qc.invalidateQueries({ queryKey: ['pr'] });
      if (editingId) {
        toast.push(`PR ${pr.prNo} updated`, 'success');
      } else {
        toast.pushStep(`PR ${pr.prNo} created`, {
          body: 'Next step — approve and send RFQ to vendors.',
          href: '/purchase/pr',
          nextLabel: 'Open PR list',
          tag: `pr-created-${pr.id}`,
        });
      }
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (!fe.validate({
      department: !form.department && 'Department is required',
    })) return;
    const valid = form.items.filter(
      (it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim(),
    );
    if (valid.length === 0) {
      toast.push('Add at least one valid item', 'error');
      return;
    }
    setTriedSave(true);
    const unpriced = valid.filter((it) => !(Number(it.unitPrice) > 0));
    if (unpriced.length) {
      toast.push(`Unit price is required — ${unpriced.map((it) => it.itemName.trim()).join(', ')}`, 'error');
      return;
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingId ? `Edit PR — ${editing?.prNo}` : 'New Purchase Requisition'}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending || !!isReadOnly}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save PR</span>)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="PR Number">
          <Input value={editing?.prNo ?? 'Auto'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input
            type="date"
            value={form.prDate}
            onChange={(e) => setForm({ ...form, prDate: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="MRN Reference">
          <Select
            value={String(form.mrnId)}
            onChange={(e) => {
              const v = e.target.value;
              const mrn = approvedMrns.find((m) => String(m.id) === v);
              setForm({
                ...form,
                mrnId: v ? Number(v) : '',
                department: mrn?.department ?? form.department,
                // Subject carries forward from the MRN (still editable here).
                subject: mrn?.subject || form.subject,
                // Auto-populate items from the MRN (only if user hasn't started filling yet).
                items:
                  mrn && form.items.length === 1 && !form.items[0].itemName
                    ? mrn.items.map((it) => ({
                        itemId: it.itemId,
                        itemName: it.itemName,
                        qty: it.qty,
                        unit: it.unit,
                        unitPrice: purchaseRateOf(it.itemId),
                      }))
                    : form.items,
              });
            }}
            disabled={!!isReadOnly}
          >
            <option value="">— None —</option>
            {approvedMrns.map((m) => (
              <option key={m.id} value={m.id}>
                {m.mrnNo} — {m.department}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Department" required error={fe.errors.department}>
          <SmartSelect
            listKey="dept"
            value={form.department}
            onChange={(v) => { setForm({ ...form, department: v }); fe.clear('department'); }}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Subject" className="col-span-2">
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Copied from the MRN — optional"
            maxLength={200}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Approval Authority">
          <Select
            value={form.authority}
            onChange={(e) => setForm({ ...form, authority: e.target.value as Authority })}
            disabled={!!isReadOnly}
          >
            <option>HOD</option>
            <option>Manager</option>
            <option>Director</option>
            <option>Chairman</option>
          </Select>
        </FormGroup>
        <FormGroup label="Suggested Vendor">
          <SearchableSelect
            options={vendorOptions}
            value={form.vendorId === '' ? '' : form.vendorId}
            onChange={(val) => setForm({ ...form, vendorId: val ? Number(val) : '' })}
            placeholder="Optional — prefills the PO"
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Remarks" className="col-span-2">
          <Textarea
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
      </div>

      <ItemsTable<PrItem>
        title="Items to Procure"
        rows={form.items}
        onChange={(items) => setForm({ ...form, items })}
        newRow={() => ({ itemName: '', qty: '', unit: '', unitPrice: '' })}
        totalLabel="Estimated Total"
        totalValue={`₹${formatINR(total)}`}
        columns={[
          {
            key: 'item',
            header: 'Item',
            cell: (row, _i, update) => (
              <CategoryItemPicker
                showImage
                showBarcode={false}
                items={itemMaster.map((it) => ({
                  id: it.id,
                  code: it.code,
                  name: it.name,
                  category: it.category,
                  currentStock: 0,
                  imageUrl: it.imageUrl,
                  barcode: it.barcode,
                }))}
                value={row.itemId ?? '__custom__'}
                disabled={!!isReadOnly}
                onCustom={() => update({ itemId: null })}
                onPick={(id) => {
                  const it = itemMaster.find((i) => i.id === id);
                  update({
                    itemId: id,
                    itemName: it?.name ?? row.itemName,
                    unit: it?.unit ?? row.unit,
                    unitPrice: it ? purchaseRateOf(id) : row.unitPrice,
                  });
                }}
              />
            ),
          },
          {
            key: 'barcode',
            header: 'SKU Barcode',
            width: '130px',
            cell: (row) => {
              const bc = itemMaster.find((i) => i.id === row.itemId)?.barcode;
              return <span className="font-mono text-xs text-[var(--text-muted)]">{bc || '—'}</span>;
            },
          },
          {
            key: 'itemName',
            header: 'Description',
            cell: (row, _i, update) => (
              <Input
                value={row.itemName}
                onChange={(e) => update({ itemName: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'qty',
            header: 'Qty',
            width: '90px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={row.qty as string}
                onChange={(e) => update({ qty: e.target.value })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'unit',
            header: 'Unit',
            width: '110px',
            cell: (row, _i, update) => (
              <Input
                value={row.unit}
                onChange={(e) => update({ unit: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'unitPrice',
            header: <>Unit Price (₹)<span className="text-danger"> *</span></>,
            width: '120px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                step="0.01"
                value={String(row.unitPrice ?? '')}
                onChange={(e) => update({ unitPrice: e.target.value })}
                invalid={triedSave && !!row.itemName.trim() && !(Number(row.unitPrice) > 0)}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'rowTotal',
            header: 'Total',
            width: '110px',
            align: 'right',
            cell: (row) => (
              <span className="font-bold text-accent">
                ₹{formatINR(Number(row.qty || 0) * Number(row.unitPrice || 0))}
              </span>
            ),
          },
        ]}
      />
    </Modal>
  );
}

function toPayload(f: FormState) {
  return {
    prDate: f.prDate,
    subject: f.subject.trim(),
    mrnId: typeof f.mrnId === 'number' ? f.mrnId : undefined,
    vendorId: typeof f.vendorId === 'number' ? f.vendorId : undefined,
    department: f.department,
    authority: f.authority,
    remarks: f.remarks.trim() || undefined,
    items: f.items
      .filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim())
      .map((it) => ({
        itemId: it.itemId ?? undefined,
        itemName: it.itemName.trim(),
        qty: Number(it.qty),
        unit: it.unit.trim(),
        unitPrice: Number(it.unitPrice ?? 0),
      })),
  };
}
